'use client';

import React, { Suspense, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF, OrbitControls, Environment, Center } from '@react-three/drei';
import * as THREE from 'three';

interface MannequinModelProps {
  url: string;
}

function MannequinModel({ url }: MannequinModelProps) {
  const { scene } = useGLTF(url);
  const modelRef = useRef<THREE.Group>(null);

  // Add subtle rotation animation
  useFrame((state) => {
    if (modelRef.current) {
      modelRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.3) * 0.1;
    }
  });

  return (
    <Center>
      <primitive 
        ref={modelRef}
        object={scene} 
        scale={[1.5, 1.5, 1.5]}
        position={[0, -1, 0]}
      />
    </Center>
  );
}

function LoadingFallback() {
  return (
    <mesh>
      <boxGeometry args={[1, 2, 0.5]} />
      <meshStandardMaterial color="#9333ea" />
    </mesh>
  );
}

interface Mannequin3DProps {
  className?: string;
  showCreateButton?: boolean;
  onCreateClick?: () => void;
}

export default function Mannequin3D({ 
  className, 
  showCreateButton = false, 
  onCreateClick 
}: Mannequin3DProps) {
  const [showButton, setShowButton] = React.useState(false);

  return (
    <div
      className={`relative w-full overflow-hidden ${className || 'h-96 bg-gradient-to-br from-blue-50 to-primary-50 rounded-xl'}`}
      onMouseEnter={() => showCreateButton && setShowButton(true)}
      onMouseLeave={() => showCreateButton && setShowButton(false)}
    >
      <Canvas
        camera={{
          position: [0, 0, 5],
          fov: 50,
          near: 0.1,
          far: 1000
        }}
        style={{ width: '100%', height: '100%' }}
        gl={{
          antialias: true,
          alpha: true,
          premultipliedAlpha: false
        }}
        onCreated={({ gl }) => {
          gl.setClearColor(0x000000, 0); // Transparent background
        }}
      >
        {/* Lighting */}
        <ambientLight intensity={0.6} />
        <directionalLight 
          position={[10, 10, 5]} 
          intensity={1}
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
        />
        <pointLight position={[-10, -10, -10]} intensity={0.3} />

        {/* Environment for better lighting */}
        <Environment preset="studio" />

        {/* 3D Model with proper Suspense inside Canvas */}
        <Suspense fallback={<LoadingFallback />}>
          <MannequinModel url="/Mannequin.glb" />
        </Suspense>

        {/* Controls */}
        <OrbitControls 
          enablePan={false}
          enableZoom={true}
          enableRotate={true}
          minDistance={3}
          maxDistance={8}
          minPolarAngle={Math.PI / 6}
          maxPolarAngle={Math.PI - Math.PI / 6}
        />
      </Canvas>

      {/* Create Avatar Button Overlay */}
      {showCreateButton && showButton && (
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center transition-all duration-300">
          <button 
            onClick={onCreateClick}
            className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-200 transform hover:scale-105"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Your Avatar
          </button>
        </div>
      )}

      {/* Tooltip */}
      {showCreateButton && (
        <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded text-sm">
          Create your own!
        </div>
      )}
    </div>
  );
}

// Preload the GLB file
useGLTF.preload('/Mannequin.glb');